/**
 * src/utils/mapAnimation.ts
 *
 * Pure helpers for the cinematic AI-agent teleport mode (and any
 * other camera animation that wants the same primitives).  Keep
 * this file dependency-free — no React, no app state — so the math
 * can be unit-tested without spinning up a Mapbox instance.
 *
 * The `safe*` wrappers are the only place we touch `mapboxgl.Map`.
 * They guard every call with `map && map.isStyleLoaded()` plus a
 * caller-provided `enabled` predicate so animation timers that
 * outlive a phase change (or an unmount) cannot fire camera moves
 * after the user has exited teleport mode.
 */

import type * as MapboxGl from 'mapbox-gl';

export interface LngLat {
  lng: number;
  lat: number;
}

const TO_RAD = Math.PI / 180;
const TO_DEG = 180 / Math.PI;
const EARTH_RADIUS_KM = 6371;

/**
 * Initial bearing from `from` → `to`, normalised to [0, 360).
 * Returns `null` when the two points are identical (no meaningful
 * heading to report — caller should keep the previous bearing).
 */
export function calculateBearing(from: LngLat, to: LngLat): number | null {
  if (!from || !to) return null;
  if (from.lat === to.lat && from.lng === to.lng) return null;
  const φ1 = from.lat * TO_RAD;
  const φ2 = to.lat   * TO_RAD;
  const Δλ = (to.lng - from.lng) * TO_RAD;
  const y  = Math.sin(Δλ) * Math.cos(φ2);
  const x  = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ  = Math.atan2(y, x);
  return ((θ * TO_DEG) + 360) % 360;
}

/** Great-circle distance in kilometres (Haversine). */
export function haversineDistanceKm(from: LngLat, to: LngLat): number {
  if (!from || !to) return 0;
  const φ1 = from.lat * TO_RAD;
  const φ2 = to.lat   * TO_RAD;
  const Δφ = (to.lat - from.lat) * TO_RAD;
  const Δλ = (to.lng - from.lng) * TO_RAD;
  const a  = Math.sin(Δφ / 2) ** 2
           + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  const c  = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c;
}

/**
 * Distance-aware teleport duration. Short hops feel snappy; long
 * hops get the full cinematic flight.
 *
 * Curve: 0 km → 2500 ms, 1 km → ~3100 ms, 10 km → ~4400 ms,
 * 100 km → ~5800 ms, 1000+ km → 7000 ms (clamped).
 */
export function getTeleportDuration(distanceKm: number): number {
  if (!Number.isFinite(distanceKm) || distanceKm <= 0) return 2500;
  const ms = 2500 + 600 * Math.log10(1 + distanceKm) * 1.6;
  return Math.max(2500, Math.min(7000, Math.round(ms)));
}

/**
 * Decide whether a hop is "long enough" to deserve the
 * zoom-out → fly → zoom-back-in teleport feel rather than a flat
 * easeTo.  Roughly: anything beyond ~30 km is a teleport, anything
 * shorter is a smooth in-place dolly.
 */
export function isLongHop(distanceKm: number): boolean {
  return Number.isFinite(distanceKm) && distanceKm >= 30;
}

const BUILDINGS_LAYER_ID = 'gt-teleport-3d-buildings';

/**
 * Idempotently add a 3D buildings fill-extrusion layer to the map
 * if the active style ships the `composite` source with a `building`
 * source-layer (Mapbox Streets and most derivatives do).  No-op for
 * styles that don't (Mapbox Standard already renders buildings
 * natively, custom styles may not have the source at all) — we
 * never throw, even if the style is mid-swap.
 *
 * Returns `true` when the layer was added (or already present),
 * `false` when the style doesn't support it.
 */
export function ensure3DBuildingsLayer(map: MapboxGl.Map | null): boolean {
  if (!map) return false;
  try {
    if (!map.isStyleLoaded()) return false;
    if (map.getLayer(BUILDINGS_LAYER_ID)) return true;
    const style = map.getStyle();
    const hasCompositeBuilding = !!(style.sources && (style.sources as any).composite);
    if (!hasCompositeBuilding) return false;
    // Mapbox-gl-js #11820: dropping a fill-extrusion layer in
    // synchronously while the caller may still be inside a
    // `styledata` / `style.load` / `moveend` event dispatch leaves
    // the new layer's internal Layout uninitialised, which then
    // crashes the very next render at
    // `layer.layout.get('fill-extrusion-edge-radius')`. Schedule
    // the actual addLayer one macrotask later so the style queue
    // can drain first.
    setTimeout(() => {
      try {
        if (!map || map.getLayer(BUILDINGS_LAYER_ID)) return;
        const liveStyle = map.getStyle();
        if (!liveStyle?.sources || !(liveStyle.sources as any).composite) return;
        const labelLayer = liveStyle.layers?.find(
          (l: any) => l.type === 'symbol' && (l.layout as any)?.['text-field'],
        );
        map.addLayer(
          {
            id: BUILDINGS_LAYER_ID,
            source: 'composite',
            'source-layer': 'building',
            filter: ['==', 'extrude', 'true'],
            type: 'fill-extrusion',
            minzoom: 14,
            // Explicit empty layout so the painter's
            // `layer.layout.get(...)` call always finds a Layout
            // instance on the first render after style processing.
            layout: {},
            paint: {
              'fill-extrusion-color': '#aab0c0',
              'fill-extrusion-height': [
                'interpolate', ['linear'], ['zoom'],
                15,    0,
                15.05, ['get', 'height'],
              ],
              'fill-extrusion-base': [
                'interpolate', ['linear'], ['zoom'],
                15,    0,
                15.05, ['get', 'min_height'],
              ],
              'fill-extrusion-opacity': 0.65,
            },
          } as any,
          labelLayer?.id,
        );
      } catch { /* style swap mid-flight; safe to bail */ }
    }, 0);
    // We've scheduled the work; report success so the caller's
    // optimistic flow (e.g. teleport HUD enabling 3D mode) can
    // proceed without a flicker.
    return true;
  } catch {
    return false;
  }
}

/** Remove the teleport buildings layer if present. Never throws. */
export function remove3DBuildingsLayer(map: MapboxGl.Map | null): void {
  if (!map) return;
  try {
    if (map.getLayer(BUILDINGS_LAYER_ID)) map.removeLayer(BUILDINGS_LAYER_ID);
  } catch { /* swallow */ }
}

/**
 * Inspect the rendered 3D buildings layer(s) and return the tallest
 * extruded `height` (in metres) within the current viewport.
 *
 * Both the teleport (`gt-teleport-3d-buildings`) and App.tsx's
 * auto-3D path (`3d-buildings`) add extrusion layers that pull from
 * the Mapbox composite "building" source-layer; we query whichever
 * are present so this helper works for callers in either subsystem.
 *
 * Returns 0 when no relevant layer is present, when the query fails
 * (style swap mid-frame, source not yet hydrated), or when the
 * viewport is empty of extruded buildings. Pure-ish: only reads map
 * state, never mutates anything.
 */
const FALLBACK_BUILDING_LAYER_IDS = [BUILDINGS_LAYER_ID, '3d-buildings'];

export function maxRenderedBuildingHeightM(
  map: MapboxGl.Map | null,
  /** Optional explicit layer-id override; defaults to all known IDs. */
  layerIds: string[] = FALLBACK_BUILDING_LAYER_IDS,
): number {
  if (!map) return 0;
  try {
    if (!map.isStyleLoaded()) return 0;
    const present = layerIds.filter((id) => {
      try { return !!map.getLayer(id); } catch { return false; }
    });
    if (present.length === 0) return 0;
    const feats = map.queryRenderedFeatures({ layers: present } as any);
    if (!feats || feats.length === 0) return 0;
    let max = 0;
    for (const f of feats) {
      const h = Number((f.properties as any)?.height);
      if (Number.isFinite(h) && h > max) max = h;
    }
    return max;
  } catch {
    return 0;
  }
}

/**
 * Pick a zoom level that fits a building of `heightM` metres on
 * screen at the teleport / auto-3D pitch (~60°). The output ramps
 * DOWN (camera backs off) as height grows so towers like the Burj
 * Khalifa fit on screen instead of stabbing through the top edge
 * at the default close zoom. Returns the default zoom untouched
 * when `heightM` is missing / non-positive (we know nothing →
 * don't second-guess the caller).
 *
 * Tuning baseline: at pitch ~60° and zoom 17 the screen covers
 * roughly 80-120 m of ground. The bands below were re-tuned after
 * field-testing Dubai / NYC / Shanghai — the previous 800 m+ band
 * dropped to 14.5 which felt like a bird's-eye yank-out on the
 * Burj Khalifa; we now keep it at 15.5 (and add a finer 800-1000 m
 * step) so the iconic skyline still reads close.
 */
export function buildingAwareZoom(heightM: number, defaultZoom: number): number {
  if (!Number.isFinite(heightM) || heightM <= 0) return defaultZoom;
  if (heightM < 80)    return defaultZoom;
  if (heightM < 200)   return Math.min(defaultZoom, 16.7);
  if (heightM < 400)   return Math.min(defaultZoom, 16.4);
  if (heightM < 600)   return Math.min(defaultZoom, 16.1);
  if (heightM < 800)   return Math.min(defaultZoom, 15.8);
  if (heightM < 1000)  return Math.min(defaultZoom, 15.5); // Burj Khalifa (828 m) lands here
  return Math.min(defaultZoom, 15.3);                      // megatall (>1 km, Jeddah etc.)
}

interface SafeAnimationOpts {
  /**
   * Caller-provided "should this animation actually run?" check.
   * Evaluated lazily so the gate sees the most recent app state
   * (phase still active, mode not exited, manual override off, …).
   * Returning `false` cancels the call without touching the map.
   */
  enabled?: () => boolean;
}

// Mapbox GL JS doesn't export FlyToOptions / EaseToOptions as named
// types — they're declared inline on Map.flyTo / Map.easeTo. We pull
// the parameter type off the method signature so we automatically
// pick up upstream typing changes without re-declaring fields.
export type FlyToOptions  = Parameters<MapboxGl.Map['flyTo']>[0];
export type EaseToOptions = Parameters<MapboxGl.Map['easeTo']>[0];

/**
 * `flyTo` / `easeTo` wrappers that no-op when:
 *   - `map` is null,
 *   - the style isn't loaded yet (Mapbox throws if you try),
 *   - the caller's `enabled()` gate returns false.
 *
 * Mapbox swallows mid-flight `flyTo` calls if the previous one is
 * still in progress — that's actually the behaviour we want for the
 * teleport phase machine, so we don't try to chain `moveend` here.
 * The caller subscribes to `map.once('moveend', …)` itself.
 */
export function safeFlyTo(
  map: MapboxGl.Map | null,
  options: FlyToOptions,
  guard?: SafeAnimationOpts,
): boolean {
  if (!map) {
    return false;
  }
  if (guard?.enabled && !guard.enabled()) {
    return false;
  }
  try {
    if (!map.isStyleLoaded()) {
      return false;
    }
    map.flyTo({ essential: true, ...options });
    return true;
  } catch (err) {
    return false;
  }
}

export function safeEaseTo(
  map: MapboxGl.Map | null,
  options: EaseToOptions,
  guard?: SafeAnimationOpts,
): boolean {
  if (!map) return false;
  if (guard?.enabled && !guard.enabled()) return false;
  try {
    if (!map.isStyleLoaded()) return false;
    map.easeTo({ essential: true, ...options });
    return true;
  } catch {
    return false;
  }
}

/** Snapshot of the current camera state — what we'll restore on exit. */
export interface CameraSnapshot {
  center:  [number, number];
  zoom:    number;
  pitch:   number;
  bearing: number;
}

export function captureCamera(map: MapboxGl.Map | null): CameraSnapshot | null {
  if (!map) return null;
  try {
    const c = map.getCenter();
    return {
      center:  [c.lng, c.lat],
      zoom:    map.getZoom(),
      pitch:   map.getPitch(),
      bearing: map.getBearing(),
    };
  } catch {
    return null;
  }
}
